import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Container,
  LinearProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Home as HomeIcon,
  ArrowBack as BackIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';

const REDIRECT_SECONDS = 5;

export const NotFound: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setElapsed((prev) => {
        if (prev + 1 >= REDIRECT_SECONDS * 10) {
          clearInterval(interval);
          return REDIRECT_SECONDS * 10;
        }
        return prev + 1;
      });
    }, 100);

    const timeout = setTimeout(() => {
      navigate('/');
    }, REDIRECT_SECONDS * 1000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [navigate]);

  const progress = (elapsed / (REDIRECT_SECONDS * 10)) * 100;
  const secondsLeft = Math.ceil(REDIRECT_SECONDS - elapsed / 10);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Container maxWidth="sm">
        <Box sx={{ textAlign: 'center' }}>
          {/* Icon */}
          <Box
            sx={{
              fontSize: { xs: 80, md: 120 },
              mb: 2,
              color: (theme) => theme.palette.background.primary,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <ErrorIcon sx={{ fontSize: 'inherit' }} />
          </Box>

          {/* 404 Text */}
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: 48, md: 64 },
              fontWeight: 700,
              mb: 1,
              background: `linear-gradient(135deg, ${theme.palette.background.primary} 0%, ${theme.palette.secondary.main} 100%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            404
          </Typography>

          {/* Title */}
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              mb: 1,
              fontSize: { xs: 24, md: 32 },
            }}
          >
            Pagina no encontrada
          </Typography>

          {/* Description */}
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              mb: 3,
              fontSize: { xs: 14, md: 16 },
            }}
          >
            Lo sentimos, la pagina que buscas no existe o ha sido movida.
          </Typography>

          {/* Redirect Progress */}
          <Box sx={{ mb: 4, px: { xs: 2, sm: 6 } }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: (t) => t.palette.action.hover,
                '& .MuiLinearProgress-bar': {
                  borderRadius: 3,
                  bgcolor: (t) => t.palette.background.primary,
                },
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Redirigiendo al inicio en {secondsLeft}s...
            </Typography>
          </Box>

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size={isMobile ? 'small' : 'medium'}
              startIcon={<HomeIcon />}
              onClick={() => navigate('/')}
            >
              Ir al Inicio
            </Button>
            <Button
              variant="outlined"
              size={isMobile ? 'small' : 'medium'}
              startIcon={<BackIcon />}
              onClick={() => navigate(-1)}
            >
              Volver Atras
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default NotFound;
